// 💾 src/App.tsx
import React from "react"
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet
} from "react-router-dom"
import { useTranslation } from "react-i18next"
import MainLayout from "./components/layout/MainLayout"
import Register from "./pages/auth/Register"
import Login from "./pages/auth/Login"
import ProtectedRoute from "./routes/ProtectedRoute"
import AdminProtectedRoute from "./routes/AdminProtectedRoute"
import Home from "./pages/public/Home"
import Market from "./pages/public/Market"
import TradePortal from "./pages/public/TradePortal"
import SpotTrade from "./pages/public/SpotTrade"
import FuturesTrade from "./pages/public/FeaturesTrade"
import AccountTypes from "./pages/public/AccountTypes"
import About from "./pages/public/About"
import Contact from "./pages/public/Contact"
import News from "./pages/public/News"
import Earn from "./pages/public/Earn"
import Events from "./pages/public/Events"
import Profile from "./pages/client/sections/ProfileIndex"
import Portfolio from "./pages/client/sections/PortfolioIndex"
import Trades from "./pages/client/sections/Trades"
import Deposit from "./pages/client/sections/Deposit"
import Withdraw from "./pages/client/sections/Withdraw"
import Notifications from "./pages/client/sections/Notifications"
import VerificationPage from "./pages/verify/VerificationPage"
import AdminDashboard from "./pages/admin/Dashboard"
import { ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import AdminLogin from "./pages/admin/AdminLogin"

const App: React.FC = () => {
  const { i18n } = useTranslation()

  return (
    <Router>
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />

      <Routes>
        {/* Root → default language */}
        <Route path="/" element={<Navigate to="/en" replace />} />

        {/* Client-facing routes (localized) */}
        <Route path=":lng" element={<MainLayout />}>
          <Route index element={<Home />} />
          <Route path="markets" element={<Market />} />
          <Route path="exchange/:pair" element={<SpotTrade />} />
          <Route path="trade">
            <Route path="portal" element={<TradePortal />} />
            <Route path="spot" element={<SpotTrade />} />
            <Route path="futures" element={<FuturesTrade />} />
          </Route>
          <Route path="account-types" element={<AccountTypes />} />
          <Route path="contact" element={<Contact />} />
          <Route path="news" element={<News />} />
          <Route path="earn" element={<Earn />} />
          <Route path="events" element={<Events />} />
          <Route path="about-us" element={<About />} />
          <Route path="register" element={<Register />} />
          <Route path="login" element={<Login />} />

          {/* Verification */}
          <Route
            path="verify"
            element={
              <ProtectedRoute requiredRole="client" requireVerified={false}>
                <VerificationPage />
              </ProtectedRoute>
            }
          />

          {/* Client area */}
          <Route
            path="user"
            element={
              <ProtectedRoute requiredRole="client" requireVerified={false}>
                <Outlet />
              </ProtectedRoute>
            }
          >
            <Route path="profile" element={<Profile />} />
            <Route path="portfolio" element={<Portfolio />} />
            <Route path="trades" element={<Trades />} />
            <Route path="deposit" element={<Deposit />} />
            <Route path="withdraw" element={<Withdraw />} />
            <Route path="notifications" element={<Notifications />} />
          </Route>

          {/* Localized 404 */}
          <Route path="*" element={<div>404 Not Found</div>} />
        </Route>

        {/* Admin routes (NOT localized) */}
        <Route
          path="/admin/dashboard"
          element={
            <AdminProtectedRoute>
              <AdminDashboard />
            </AdminProtectedRoute>
          }
        />
        <Route path="/admin/login" element={<AdminLogin />} />

        {/* Global 404 */}
        <Route path="*" element={<div>404 Not Found</div>} />
      </Routes>
    </Router>
  )
}

export default App
